import { pumsemImageUrl } from '../lib/pumsemAsset';
import type { PumsemBlock, PumsemListStyle } from '../model/pumsem.type';
import { PumsemTable } from './PumsemTable';

interface PumsemBodyProps {
  body: PumsemBlock[];
  /** 표 행 배지에 붙일 판 표기(`'26상`). */
  editionLabel?: string;
  /** 개정 표시를 끈다. 비교 화면의 '이전 판' 쪽처럼 표기가 무의미한 자리. */
  hideChanges?: boolean;
  /** 중첩 깊이. 들여쓰기를 정한다. */
  depth?: number;
}

/**
 * 본문 렌더러 — 문단·번호목록·표·그림·산식을 **원본 순서 그대로** 그린다.
 *
 * 번호는 데이터가 갖는다(`item.marker`). `<ol>` 의 자동 번호를 쓰지 않는 이유:
 * 발행본에는 개정으로 번호가 건너뛴 목록이 있고, 실무자는 "적용방법 7항" 처럼
 * 번호로 인용한다 — 화면이 다시 매기면 원문과 조용히 어긋난다.
 */
export function PumsemBody({
  body,
  editionLabel,
  hideChanges = false,
  depth = 0,
}: PumsemBodyProps) {
  if (body.length === 0) return null;

  return (
    <div className={depth === 0 ? 'mt-5 space-y-3' : 'mt-2 space-y-2'}>
      {body.map((block, index) => (
        <Block
          // 블록에는 안정적인 id 가 없다. 순서가 곧 정체성이라 인덱스가 맞는
          // 키다(정렬·필터가 없는 정적 목록이다).
          key={`${block.type}-${index}`}
          block={block}
          editionLabel={editionLabel}
          hideChanges={hideChanges}
          depth={depth}
        />
      ))}
    </div>
  );
}

function Block({
  block,
  editionLabel,
  hideChanges,
  depth,
}: {
  block: PumsemBlock;
  editionLabel?: string;
  hideChanges: boolean;
  depth: number;
}) {
  switch (block.type) {
    case 'paragraph':
      return (
        <p className="flex gap-2.5 text-[15.5px] leading-7 text-neutral-700 dark:text-neutral-300">
          {/* 글머리는 화면이 그린다 — 데이터에 남기면 두 번 찍힌다. */}
          {depth === 0 && (
            <span aria-hidden className="text-neutral-400">
              ◦
            </span>
          )}
          <span className="min-w-0">{block.text}</span>
        </p>
      );

    case 'list':
      return (
        <ul className="space-y-1.5">
          {block.items.map((item, itemIndex) => (
            // 번호가 중복되는 목록이 원본에 있다(개정으로 겹친 경우).
            // 정렬·필터가 없는 정적 목록이라 인덱스가 맞는 키다.
            <li key={itemIndex}>
              <div className="flex gap-2 text-[15.5px] leading-7 text-neutral-700 dark:text-neutral-300">
                <span className="shrink-0 tabular-nums text-neutral-500 dark:text-neutral-400">
                  {markerText(block.style, item.marker)}
                </span>
                <span className="min-w-0">{item.text}</span>
              </div>
              <div className="pl-5">
                <PumsemBody
                  body={item.children}
                  editionLabel={editionLabel}
                  hideChanges={hideChanges}
                  depth={depth + 1}
                />
              </div>
            </li>
          ))}
        </ul>
      );

    case 'table':
      return (
        <PumsemTable
          table={block.table}
          editionLabel={editionLabel}
          hideChanges={hideChanges}
        />
      );

    case 'figure':
      // 주소가 비어 있으면 그리지 않는다 — 빈 키로 URL 을 만들면 CDN 루트를
      // 가리키는 깨진 이미지가 본문 한가운데 남는다.
      if (!block.src.trim()) return null;
      return (
        <figure className="my-4">
          <img
            src={pumsemImageUrl(block.src, { format: 'auto' })}
            alt={block.alt}
            loading="lazy"
            className="mx-auto max-w-full rounded-xl border border-neutral-200 dark:border-white/12"
          />
          {block.caption && (
            <figcaption className="mt-2 text-center text-[13.5px] text-neutral-500">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );

    case 'formula':
      return (
        <div className="my-4 rounded-xl border border-neutral-200 px-4 py-3 dark:border-white/12">
          {/* 산식은 줄바꿈과 기호 간격이 의미다 — 등폭으로, 접지 않고 그린다. */}
          <p className="overflow-x-auto font-mono text-[15px] leading-7 whitespace-pre text-neutral-900 dark:text-neutral-100">
            {block.text}
          </p>
          {block.legend.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-[13.5px] leading-6 text-neutral-600 dark:text-neutral-400">
              {block.legend.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      );

    case 'notes':
      if (block.notes.length === 0) return null;
      return <PumsemNotes notes={block.notes} />;
  }
}

/**
 * [주] 묶음. 본문 자리에 놓인 블록과 항목 맨 아래의 배치 안 한 [주]가 같은
 * 모양을 쓴다 — 둘이 다르게 생기면 같은 [주]인데 다른 것처럼 읽힌다.
 */
export function PumsemNotes({ notes }: { notes: string[] }) {
  return (
    <section className="rounded-xl border border-neutral-200 px-4 py-3 dark:border-white/12">
      <h3 className="mb-1.5 text-[12.5px] font-bold tracking-wide text-neutral-400">
        [주]
      </h3>
      <ul className="space-y-1 text-[14.5px] leading-7 text-neutral-700 dark:text-neutral-300">
        {notes.map((note, index) => (
          // 같은 문장의 [주]가 한 묶음에 두 번 있을 수 있다. 정적 목록이라 인덱스가 맞다.
          <li key={index}>{note}</li>
        ))}
      </ul>
    </section>
  );
}

/** 스타일에 맞는 마침 기호를 붙인다. 번호 자체는 데이터가 준 값 그대로다. */
function markerText(style: PumsemListStyle, marker: string): string {
  switch (style) {
    case 'decimal':
    case 'hangul':
      return `${marker}.`;
    case 'paren':
    case 'paren-hangul':
      return `${marker})`;
    case 'circled':
      return marker;
  }
}
